"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { categoryLabels } from "@/lib/categoryLabels";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import RouteGuard from "@/components/RouteGuard";
import { getItemRequests, createItemRequest } from "@/lib/itemRequestsAPI";
import useSWR from "swr";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import HomepageDemandStrip from "@/components/homepage/HomepageDemandStrip";
import RequestItemCaptureForm, {
  type RequestItemCaptureFormValues,
} from "@/components/ui/RequestItemCaptureForm";
import { trackMetaLead } from "@/lib/metaPixel";
import { trackClarityLead } from "@/lib/clarity";
import { navigateToHome } from "@/lib/navigation/authRedirect";
import { RequestItemRouteProviders } from "./RequestItemRouteProviders";
import { HOMEPAGE_COPY } from "@/lib/homepage/copy";

function RequestItemPageContent() {
  const router = useRouter();
  const { showToast } = useToast();
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const { recentItemRequestsEnabled, isLoading: systemSettingsLoading } =
    useSystemSettings();
  const canAccess = recentItemRequestsEnabled || isAdmin;
  const [isLoading, setIsLoading] = useState(false);
  const [showPublicBoard, setShowPublicBoard] = useState(false);

  const {
    data: itemRequests = [],
    error,
    mutate,
  } = useSWR(
    canAccess && showPublicBoard ? "item-requests" : null,
    getItemRequests,
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000,
      shouldRetryOnError: true,
      errorRetryCount: 3,
    },
  );

  useEffect(() => {
    if (!authLoading && !systemSettingsLoading && !canAccess) {
      showToast("Item requests are currently disabled.", "info");
      navigateToHome(router, { replace: true });
    }
  }, [authLoading, systemSettingsLoading, canAccess, router, showToast]);

  if (systemSettingsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-2xl font-semibold text-foreground mb-2">
          Item requests are paused
        </h1>
        <p className="text-text-light">
          Check back later, or contact support if you need help.
        </p>
      </div>
    );
  }

  const handleSubmit = async (values: RequestItemCaptureFormValues) => {
    if (!user) {
      showToast("Please log in to submit a request", "error");
      return;
    }

    setIsLoading(true);
    try {
      const created = await createItemRequest(values);

      trackMetaLead({
        contentName: created.title,
        contentCategory: categoryLabels[created.category] ?? created.category,
        ...(created.maxPrice !== undefined
          ? { valueNgn: created.maxPrice }
          : {}),
      });
      trackClarityLead({
        contentName: created.title,
        contentCategory: categoryLabels[created.category] ?? created.category,
        ...(created.maxPrice !== undefined
          ? { valueNgn: created.maxPrice }
          : {}),
      });

      showToast(
        "Request submitted — open My Requests anytime to see matching listings.",
        "success",
      );
      mutate();
      router.push("/my-requests");
    } catch (err) {
      console.error("Error submitting item request:", err);
      const message =
        err instanceof Error
          ? err.message
          : "Failed to submit request. Please try again.";
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold text-foreground tracking-tight">
          What are you looking for?
        </h1>
        <p className="mt-2 text-text-light text-base max-w-xl">
          Tell us in a few steps. We&apos;ll alert you when something matching
          is approved near you — and you can browse matches on My Requests.
        </p>
        <p className="mt-3 text-sm text-text-light">
          Already requested something?{" "}
          <Link
            href="/my-requests"
            className="text-primary font-medium hover:underline"
          >
            Open My Requests
          </Link>
        </p>
      </header>

      <section aria-label="Create request" className="mb-10">
        <RequestItemCaptureForm
          user={user}
          isLoading={isLoading}
          onSubmit={handleSubmit}
        />
      </section>

      {/* Public board is secondary (D4 private-first) */}
      <section className="border-t border-border pt-8">
        <button
          type="button"
          onClick={() => setShowPublicBoard((v) => !v)}
          className="text-sm text-text-light hover:text-foreground transition-colors"
        >
          {showPublicBoard
            ? "Hide other people’s requests"
            : "Browse other people’s requests (optional)"}
        </button>

        {showPublicBoard ? (
          <div className="mt-4">
            {error ? (
              <p className="text-sm text-red-600">
                Couldn’t load the public list. Try again later.
              </p>
            ) : (
              <HomepageDemandStrip
                requests={itemRequests}
                title={HOMEPAGE_COPY.shelves.recentRequests}
                emptyTitle="No other public requests yet"
                emptyBody="When buyers request items, they show up here for sellers to match."
                emptyCtaHref="/my-requests"
                emptyCtaLabel="Open My Requests"
                showRequestLink={false}
                showEmptyCta={false}
              />
            )}
            {isAdmin ? (
              <p className="mt-3 text-xs text-text-light">
                Prefill /sell from a row is a seller side-path — not the main
                buyer loop.
              </p>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default function RequestItemPage() {
  return (
    <RouteGuard>
      <RequestItemRouteProviders>
        <ErrorBoundary>
          <RequestItemPageContent />
        </ErrorBoundary>
      </RequestItemRouteProviders>
    </RouteGuard>
  );
}
